// src/components/home/TenantBookingPage.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone, Mail, Globe, MapPin, Star, ChevronRight, ChevronLeft, ChevronDown, Scissors, PenLine, X, LogOut, CalendarCheck, LayoutDashboard, Share2, Check } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../context/AuthContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { StarRating, ServiceVisual, TenantUnavailable } from "../ui";
import ReviewModal from "../booking/ReviewModal";
import { displayPhone } from "../../utils/phone";

const ALL_TABS = ["Services", "Team", "Gallery", "Reviews", "About", "Address"];

// ── Helpers ───────────────────────────────────────────────────────────────────
const DAY_KEYS    = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS  = {
  sun: "Sunday", mon: "Monday", tue: "Tuesday", wed: "Wednesday",
  thu: "Thursday", fri: "Friday", sat: "Saturday",
};
const DISPLAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function timeToMins(t) {
  if (!t) return 0;
  const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = parseInt(m[1]);
  if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
  if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
  return h * 60 + parseInt(m[2]);
}

function getTodayStatus(businessHours) {
  if (!businessHours) return null;
  const now      = new Date();
  const nowMins  = now.getHours() * 60 + now.getMinutes();
  const todayIdx = now.getDay();
  const today    = businessHours[DAY_KEYS[todayIdx]];

  // Open right now
  if (today?.open && nowMins >= timeToMins(today.start) && nowMins < timeToMins(today.end)) {
    return { isOpen: true, closeTime: today.end };
  }
  // Closed now — opens later today
  if (today?.open && nowMins < timeToMins(today.start)) {
    return { isOpen: false, nextOpen: { time: today.start, label: "today" } };
  }
  // Closed now — find the next open day within a week
  for (let i = 1; i <= 7; i++) {
    const idx = (todayIdx + i) % 7;
    const d   = businessHours[DAY_KEYS[idx]];
    if (d?.open) {
      return { isOpen: false, nextOpen: { time: d.start, label: i === 1 ? "tomorrow" : DAY_LABELS[DAY_KEYS[idx]] } };
    }
  }
  return { isOpen: false, nextOpen: null }; // never open
}

function timeAgo(ts) {
  if (!ts?.seconds) return "";
  const secs = Math.floor(Date.now() / 1000) - ts.seconds;
  if (secs < 60)       return "just now";
  if (secs < 3600)     return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)    return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800)   return `${Math.floor(secs / 86400)}d ago`;
  if (secs < 2592000)  return `${Math.floor(secs / 604800)} weeks ago`;
  if (secs < 31536000) return `${Math.floor(secs / 2592000)} months ago`;
  return `${Math.floor(secs / 31536000)}y ago`;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TenantBookingPage() {
  const { tenant }          = useTenant();
  const { slug }            = useParams();
  const navigate            = useNavigate();
  const { user, isAdmin, profile, logout } = useAuth();

  const [activeTab,     setActiveTab]     = useState("Services");
  const [lightbox,      setLightbox]      = useState(null); // open photo index (null = closed)
  const [reviews,       setReviews]       = useState([]);
  const [expanded,      setExpanded]      = useState({});
  const [hoursOpen,     setHoursOpen]     = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null); // eligible booking for review
  const [reviewMsg,     setReviewMsg]     = useState("");   // info msg if no eligible booking
  const [reviewedIds,   setReviewedIds]   = useState(new Set());
  const [userReview,    setUserReview]    = useState(null); // existing review by this user
  const [menuOpen,      setMenuOpen]      = useState(false); // account dropdown
  const [shareCopied,   setShareCopied]   = useState(false);
  const menuRef = useRef(null);

  // Share this booking page — native share sheet on mobile, copy-link on desktop.
  // Only ever shares the public URL + business name (no PII).
  async function handleShare() {
    const url = `${window.location.origin}/${slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: tenant?.name || "Book online", text: `Book with ${tenant?.name || "us"}`, url });
      } catch { /* user dismissed the share sheet */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  // Close account dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  // Gallery lightbox keyboard nav: Esc closes, ←/→ move between photos
  const galleryCount = (tenant?.gallery || []).length;
  useEffect(() => {
    if (lightbox === null) return;
    function onKey(e) {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowLeft") setLightbox(i => (i - 1 + galleryCount) % galleryCount);
      else if (e.key === "ArrowRight") setLightbox(i => (i + 1) % galleryCount);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, galleryCount]);

  // Only show Gallery tab if tenant has photos
  const gallery = tenant?.gallery || [];
  const TABS = ALL_TABS.filter(t => t !== "Gallery" || gallery.length > 0);

  const sectionRefs = {
    Services: useRef(null),
    Team:     useRef(null),
    Gallery:  useRef(null),
    Reviews:  useRef(null),
    About:    useRef(null),
    Address:  useRef(null),
  };

  // Load reviews
  function fetchReviews() {
    if (!tenant?.id) return;
    getDocs(query(
      collection(db, "reviews"),
      where("tenantId", "==", tenant.id),
      where("status",   "==", "published")
    )).then(snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReviews(data);
    }).catch(() => {});
  }

  useEffect(() => { fetchReviews(); }, [tenant?.id]);

  // Load already-reviewed booking IDs + the user's existing review for this tenant
  useEffect(() => {
    if (!user || !tenant?.id) return;
    getDocs(query(
      collection(db, "reviews"),
      where("userId",   "==", user.uid),
      where("tenantId", "==", tenant.id)
    )).then(snap => {
      setReviewedIds(new Set(snap.docs.map(d => d.data().bookingId)));
      if (!snap.empty) {
        const d = snap.docs[0];
        setUserReview({ id: d.id, ...d.data() });
      } else {
        setUserReview(null);
      }
    }).catch(() => {});
  }, [user, tenant?.id]);

  // Scroll spy — highlight active tab as user scrolls
  useEffect(() => {
    const observers = [];
    TABS.forEach(tab => {
      const el = sectionRefs[tab].current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveTab(tab); },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [tenant]);

  if (!tenant) return null;
  if (tenant.billingLocked) return <TenantUnavailable businessName={tenant.name} />;

  const status    = getTodayStatus(tenant.businessHours);
  const todayKey  = DAY_KEYS[new Date().getDay()];
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;
  const breakdown = [5, 4, 3, 2, 1].map(n => ({
    stars: n,
    count: reviews.filter(r => r.rating === n).length,
  }));

  function scrollToSection(tab) {
    setActiveTab(tab);
    sectionRefs[tab].current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleWriteReview() {
    if (!user) {
      navigate("/login", { state: { from: `/${slug}` } });
      return;
    }
    if (profile?.role === "admin" || profile?.role === "staff") {
      setReviewMsg("Only customers can leave reviews.");
      setTimeout(() => setReviewMsg(""), 4000);
      return;
    }
    // If user already has a review, open modal in edit mode
    if (userReview) {
      // Find a completed booking to attach the modal context (any will do)
      const snap = await getDocs(query(
        collection(db, "bookings"),
        where("userId",   "==", user.uid),
        where("tenantId", "==", tenant.id),
        where("status",   "==", "completed")
      ));
      const any = snap.docs[0] ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null;
      setReviewBooking(any || { id: userReview.bookingId, userId: user.uid,
        service: { name: userReview.serviceName }, barber: { name: userReview.staffName } });
      return;
    }
    // New review — find an eligible completed booking not yet reviewed
    const snap = await getDocs(query(
      collection(db, "bookings"),
      where("userId",   "==", user.uid),
      where("tenantId", "==", tenant.id),
      where("status",   "==", "completed")
    ));
    const eligible = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .find(b => !reviewedIds.has(b.id));

    if (!eligible) {
      setReviewMsg("You need a completed appointment to leave a review.");
      setTimeout(() => setReviewMsg(""), 4000);
      return;
    }
    setReviewBooking(eligible);
  }

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">

      {/* ── Mobile-only "Call us" utility bar (desktop shows it in the header) ── */}
      {tenant.phone && (
        <a
          href={`tel:${tenant.phone}`}
          className="order-first lg:hidden flex items-center justify-center gap-2 bg-white
                     border-b border-surface-100 px-4 py-2.5 text-sm text-ink-700 active:bg-surface-50"
        >
          <Phone size={14} className="text-brand-600 shrink-0" />
          <span>Call us · <span className="font-semibold text-ink-900">{displayPhone(tenant.phone)}</span></span>
        </a>
      )}

      {/* ── Sticky header: identity + nav + contact ──────────────────────────── */}
      {/* order: on mobile the hero comes first then this bar (old behaviour);
          on desktop (lg+) the full header sits above the hero. */}
      <div className="order-2 lg:order-1 sticky top-0 z-30 bg-white border-b border-surface-100 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 h-14">
          {/* Logo + business name — desktop only (mobile keeps the old bare tab bar) */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-brand-400 to-brand-600
                            flex items-center justify-center shrink-0">
              {tenant.logoUrl
                ? <img src={tenant.logoUrl} alt="" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = "none"; }} />
                : <Scissors size={15} className="text-white" />
              }
            </div>
            <span className="font-semibold text-ink-900 text-base truncate max-w-[160px]">
              {tenant.name}
            </span>
          </div>

          {/* Nav tabs */}
          <div className="flex-1 min-w-0 flex overflow-x-auto scrollbar-none">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => scrollToSection(tab)}
                className={`h-14 flex items-center px-3 text-sm font-sans whitespace-nowrap shrink-0 border-b-2 transition-colors
                  ${activeTab === tab
                    ? "border-ink-900 text-ink-900 font-semibold"
                    : "border-transparent text-ink-400 hover:text-ink-700"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Call us + account — right side (share lives on the hero) */}
          <div className="flex items-center gap-1 shrink-0">
            {tenant.phone && (
              <a
                href={`tel:${tenant.phone}`}
                className="hidden lg:flex items-center gap-1.5 text-sm text-ink-600 hover:text-brand-600
                           transition-colors px-2 py-1.5 rounded-lg hover:bg-surface-50"
              >
                <Phone size={15} className="shrink-0" />
                <span className="whitespace-nowrap">Call us · {displayPhone(tenant.phone)}</span>
              </a>
            )}
            {!user ? (
              <button
                onClick={() => navigate(`/login?tenant=${slug}&redirect=/${slug}`)}
                className="text-sm font-semibold text-brand-600 hover:text-brand-700
                           px-3 py-1.5 rounded-lg hover:bg-brand-50 transition-colors whitespace-nowrap"
              >
                Sign In
              </button>
            ) : (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-brand-600
                             text-white text-sm font-semibold flex items-center justify-center
                             shadow-sm hover:opacity-90 transition-opacity"
                  title={profile?.name || user.email}
                >
                  {(profile?.name || user.displayName || user.email || "?").charAt(0).toUpperCase()}
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-surface-200 py-1.5 z-30">
                    <div className="px-4 py-2 border-b border-surface-100">
                      <p className="text-sm font-semibold text-ink-900 truncate">{profile?.name || user.displayName || "Account"}</p>
                      <p className="text-xs text-ink-400 truncate">{user.email}</p>
                    </div>
                    {profile?.role === "customer" && (
                      <button
                        onClick={() => { setMenuOpen(false); navigate(`/${slug}/profile`); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-700 hover:bg-surface-50 transition-colors"
                      >
                        <CalendarCheck size={15} className="text-ink-400" /> My Bookings
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => { setMenuOpen(false); navigate(`/${slug}/admin`); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-700 hover:bg-surface-50 transition-colors"
                      >
                        <LayoutDashboard size={15} className="text-ink-400" /> Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => { setMenuOpen(false); logout(); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut size={15} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Hero banner ─────────────────────────────────────────────────────── */}
      {/* Fixed-height cover banner (industry standard): fills the width and
          center-crops via object-cover. Vendors should upload a banner-format
          image (~3:1); a full poster will crop top/bottom by design. */}
      <div className="order-1 lg:order-2 relative h-40 sm:h-52 bg-gradient-to-br from-brand-400 to-brand-600">
        {/* Image clipped here; the utility buttons sit outside so their dropdown isn't cut off */}
        <div className="absolute inset-0 overflow-hidden">
          {tenant.heroUrl ? (
            <img src={tenant.heroUrl} alt="" className="w-full h-full object-cover"
              onError={e => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Scissors size={44} className="text-white/40" />
            </div>
          )}
        </div>

        {/* Share this page — top-right of hero (Setmore-style) */}
        <div className="absolute top-3 right-3 z-20">
          <button
            onClick={handleShare}
            title="Share this page"
            className="w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-sm flex items-center justify-center
                       text-ink-600 hover:bg-white hover:text-brand-600 transition-colors"
          >
            {shareCopied ? <Check size={16} className="text-brand-600" /> : <Share2 size={16} />}
          </button>
        </div>
      </div>

      {/* ── Business info ────────────────────────────────────────────────────── */}
      <div className="order-3 bg-white px-4 pt-4 pb-3 border-b border-surface-100">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-brand-400 to-brand-600
                          shrink-0 shadow-sm border border-surface-100 flex items-center justify-center">
            {tenant.logoUrl
              ? <img src={tenant.logoUrl} alt="" className="w-full h-full object-cover"
                  onError={e => { e.target.style.display = "none"; }} />
              : <Scissors size={22} className="text-white" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-xl text-ink-900 leading-tight">{tenant.name}</h1>
            {avgRating && reviews.length >= 3 && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-amber-500 text-sm font-bold">{avgRating}</span>
                <StarRating rating={Math.round(avgRating)} size="sm" />
                <span className="text-ink-400 text-xs">({reviews.length})</span>
              </div>
            )}
          </div>
        </div>

        {/* Expandable hours */}
        {status !== null && tenant.businessHours && (
          <div className="mt-3">
            <button
              onClick={() => setHoursOpen(h => !h)}
              className="flex items-center gap-2 text-sm text-ink-600 hover:text-ink-900 transition-colors"
            >
              <div className={`w-2 h-2 rounded-full shrink-0 ${status.isOpen ? "bg-green-500" : "bg-red-400"}`} />
              <span>
                {status.isOpen
                  ? `Open · Closes at ${status.closeTime}`
                  : status.nextOpen
                    ? `Closed · Opens ${status.nextOpen.time} ${status.nextOpen.label}`
                    : "Closed"}
              </span>
              <ChevronDown size={14} className={`transition-transform duration-200 ${hoursOpen ? "rotate-180" : ""}`} />
            </button>
            {hoursOpen && (
              <div className="mt-3 space-y-2">
                {DISPLAY_ORDER.map(key => {
                  const d = tenant.businessHours[key];
                  return (
                    <div key={key} className={`flex justify-between text-sm
                      ${key === todayKey ? "font-bold text-ink-900" : "text-ink-500"}`}>
                      <span>{DAY_LABELS[key]}</span>
                      <span>{d?.open ? `${d.start} – ${d.end}` : "Closed"}</span>
                    </div>
                  );
                })}
                <p className="text-xs text-ink-300 pt-1">
                  Time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, " ")})
                </p>
              </div>
            )}
          </div>
        )}

        {tenant.address && (
          <div className="flex items-start gap-2 mt-2">
            <MapPin size={13} className="text-ink-400 shrink-0 mt-0.5" />
            <p className="text-xs text-ink-400 leading-snug">{tenant.address}</p>
          </div>
        )}
      </div>

      {/* ── All sections (cards on a grey page) ──────────────────────────────── */}
      <div className="order-4 px-3 sm:px-4 pt-3 space-y-3 pb-28">

        {/* SERVICES */}
        <div ref={sectionRefs.Services} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 overflow-hidden">
          <div className="px-4 pt-4 pb-1">
            <h2 className="font-semibold text-ink-900 text-base">Services</h2>
          </div>
          {tenant.services?.length > 0 && (
            <div className="divide-y divide-surface-100">
              {tenant.services.map(service => (
                <div
                  key={service.id}
                  onClick={() => navigate(`/${slug}/book?service=${service.id}`)}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50
                             active:bg-surface-100 transition-colors cursor-pointer"
                >
                  <ServiceVisual service={service} category={tenant.category} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-800 text-sm">{service.name}</div>
                    <div className="text-ink-400 text-xs mt-0.5">
                      {service.duration} mins · ${service.price}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-ink-300 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* TEAM */}
        <div ref={sectionRefs.Team} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 overflow-hidden">
          {tenant.staff?.length > 0 && (
            <>
              <div className="px-4 pt-4 pb-2">
                <h2 className="font-semibold text-ink-900 text-base">Team</h2>
              </div>
              <div className="divide-y divide-surface-100">
                {tenant.staff.map(member => (
                  <div
                    key={member.id}
                    onClick={() => navigate(`/${slug}/book?barber=${member.id}`)}
                    className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50
                               active:bg-surface-100 transition-colors cursor-pointer"
                  >
                    {member.photoUrl
                      ? <img src={member.photoUrl} alt={member.name}
                             className="w-12 h-12 rounded-full object-cover shrink-0"
                             onError={e => { e.target.style.display = "none"; }} />
                      : <div className={`w-12 h-12 rounded-full bg-gradient-to-br
                                        ${member.color || "from-brand-400 to-brand-600"}
                                        flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                          {member.initials || member.name?.[0]}
                        </div>
                    }
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-800 text-sm">{member.name}</div>
                      {member.specialty && (
                        <div className="text-ink-400 text-xs mt-0.5">{member.specialty}</div>
                      )}
                      {member.bio && (
                        <div className="text-ink-400 text-xs mt-0.5 truncate">{member.bio}</div>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-ink-300 shrink-0" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* GALLERY */}
        {gallery.length > 0 && (
          <div ref={sectionRefs.Gallery} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 px-4 py-5">
            <h2 className="font-semibold text-ink-900 text-base mb-4">Gallery</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1.5 sm:gap-2">
              {gallery.map((photo, idx) => (
                <button
                  key={photo.path}
                  onClick={() => setLightbox(idx)}
                  className="aspect-square rounded-xl overflow-hidden bg-surface-100 focus:outline-none"
                >
                  <img src={photo.url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-200" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* REVIEWS */}
        <div ref={sectionRefs.Reviews} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 px-4 py-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-ink-900 text-base">Reviews</h2>
            <div className="flex items-center gap-2">
              {reviewMsg && (
                <span className="text-xs text-ink-400">{reviewMsg}</span>
              )}
              {/* Only customers (and guests) review — hide for the business's own admin/staff */}
              {profile?.role !== "admin" && profile?.role !== "staff" && (
                <button
                  onClick={handleWriteReview}
                  className="flex items-center gap-1.5 text-sm text-ink-600 border border-surface-200
                             px-3 py-1.5 rounded-full hover:bg-surface-50 transition-colors font-medium"
                >
                  <PenLine size={13} /> {userReview ? "Edit review" : "Write a review"}
                </button>
              )}
            </div>
          </div>

          {reviews.length > 0 ? (
            <>
              {/* Summary */}
              <div className="flex items-center gap-6 mb-6">
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <div className="font-semibold text-5xl text-ink-900 font-bold">{avgRating}</div>
                  <StarRating rating={Math.round(avgRating)} size="md" />
                  <div className="text-ink-400 text-xs">{reviews.length} reviews</div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {breakdown.map(({ stars, count }) => (
                    <div key={stars} className="flex items-center gap-2">
                      <span className="text-xs text-ink-400 w-3">{stars}</span>
                      <Star size={10} className="text-amber-400 fill-amber-400 shrink-0" />
                      <div className="flex-1 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 rounded-full"
                          style={{ width: reviews.length ? `${(count / reviews.length) * 100}%` : "0%" }}
                        />
                      </div>
                      <span className="text-xs text-ink-400 w-5 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="divide-y divide-surface-100">
                {reviews.map(r => (
                  <div key={r.id} className="py-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm text-ink-800">{r.customerName}</span>
                      <span className="text-xs text-ink-400">{timeAgo(r.createdAt)}</span>
                    </div>
                    <StarRating rating={r.rating} size="sm" />
                    {r.comment && (
                      <div className="mt-2">
                        <p className={`text-sm text-ink-600 leading-relaxed ${!expanded[r.id] ? "line-clamp-3" : ""}`}>
                          {r.comment}
                        </p>
                        {r.comment.length > 120 && (
                          <button
                            onClick={() => setExpanded(e => ({ ...e, [r.id]: !e[r.id] }))}
                            className="text-xs text-ink-400 mt-1 underline"
                          >
                            {expanded[r.id] ? "Show less" : "Show more"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-center text-ink-400 text-sm py-10">No reviews yet. Be the first!</p>
          )}
        </div>

        {/* ABOUT */}
        <div ref={sectionRefs.About} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 px-4 py-5 space-y-6">
          <h2 className="font-semibold text-ink-900 text-base">About</h2>

          {tenant.about && (
            <p className="text-sm text-ink-500 leading-relaxed">{tenant.about}</p>
          )}

          <div>
            <h3 className="font-semibold text-ink-800 text-sm mb-3">Contact us</h3>
            <div className="space-y-3">
              {tenant.phone && (
                <a href={`tel:${tenant.phone}`}
                   className="flex items-center gap-3 text-sm text-ink-600 hover:text-brand-600 transition-colors">
                  <Phone size={16} className="text-ink-400 shrink-0" />
                  <span>{displayPhone(tenant.phone)}</span>
                </a>
              )}
              {tenant.email && (
                <a href={`mailto:${tenant.email}`}
                   className="flex items-center gap-3 text-sm text-ink-600 hover:text-brand-600 transition-colors">
                  <Mail size={16} className="text-ink-400 shrink-0" />
                  <span>{tenant.email}</span>
                </a>
              )}
              {tenant.website && (
                <a href={tenant.website} target="_blank" rel="noreferrer"
                   className="flex items-center gap-3 text-sm text-ink-600 hover:text-brand-600 transition-colors">
                  <Globe size={16} className="text-ink-400 shrink-0" />
                  <span>{tenant.website}</span>
                </a>
              )}
            </div>
          </div>

        </div>

        {/* ADDRESS */}
        <div ref={sectionRefs.Address} className="scroll-mt-16 bg-white rounded-2xl border border-surface-100 px-4 py-5 space-y-4">
          <h2 className="font-semibold text-ink-900 text-base">Address</h2>
          {tenant.address && (
            <>
              <div className="flex items-start gap-2 text-sm text-ink-600">
                <MapPin size={16} className="text-ink-400 shrink-0 mt-0.5" />
                <span>{tenant.address}</span>
              </div>
              <a
                href={`https://maps.google.com?q=${encodeURIComponent(tenant.address)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl
                           border border-surface-200 text-sm text-ink-600 font-medium
                           hover:bg-surface-50 transition-colors"
              >
                <MapPin size={15} className="text-brand-500" />
                Open in Google Maps
              </a>
            </>
          )}
        </div>

      </div>

      {/* ── Floating Book button ──────────────────────────────────────────────── */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30">
        <button
          onClick={() => navigate(`/${slug}/book`)}
          className="bg-ink-900 text-white font-semibold px-12 py-3.5 rounded-full shadow-xl
                     hover:bg-ink-700 active:scale-95 transition-all text-sm tracking-wide"
        >
          Book
        </button>
      </div>

      {/* ── Lightbox carousel ────────────────────────────────────────────────── */}
      {lightbox !== null && gallery[lightbox] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 sm:p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-10"
            onClick={() => setLightbox(null)}
          >
            <X size={28} />
          </button>

          {gallery.length > 1 && (
            <button
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full
                         bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              onClick={e => { e.stopPropagation(); setLightbox(i => (i - 1 + gallery.length) % gallery.length); }}
            >
              <ChevronLeft size={24} />
            </button>
          )}

          <img
            src={gallery[lightbox].url}
            alt=""
            className="max-w-full max-h-full rounded-xl object-contain"
            onClick={e => e.stopPropagation()}
          />

          {gallery.length > 1 && (
            <button
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full
                         bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              onClick={e => { e.stopPropagation(); setLightbox(i => (i + 1) % gallery.length); }}
            >
              <ChevronRight size={24} />
            </button>
          )}

          {gallery.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm font-sans">
              {lightbox + 1} / {gallery.length}
            </div>
          )}
        </div>
      )}

      {/* ── Review modal ─────────────────────────────────────────────────────── */}
      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          tenantId={tenant.id}
          existingReview={userReview || undefined}
          onClose={() => setReviewBooking(null)}
          onSuccess={() => {
            setReviewedIds(s => new Set([...s, reviewBooking.id]));
            setReviewBooking(null);
            fetchReviews();
            // Refresh userReview so edit state stays accurate
            if (user && tenant?.id) {
              getDocs(query(
                collection(db, "reviews"),
                where("userId",   "==", user.uid),
                where("tenantId", "==", tenant.id)
              )).then(snap => {
                if (!snap.empty) {
                  const d = snap.docs[0];
                  setUserReview({ id: d.id, ...d.data() });
                }
              }).catch(() => {});
            }
          }}
        />
      )}

    </div>
  );
}
